/** @format */

import store from '@/store'
import {
  Module, VuexModule, Mutation, Action,
} from 'vuex-module-decorators'
import SelectOption from '@/models/interface/SelectOption'
import Api from '@/models/Api'
import Publisher from '@/models/Publisher'
import PaginateOptions from '@/models/interface/PaginateOptions'

@Module({
  dynamic: true, store, namespaced: true, name: 'publisher',
})
export default class PublisherModule extends VuexModule {
  public data: Publisher[] = []

  /**
   * @deprecated No longer needed after Elasticsearch implementation. Use searchOptions instead.
   */
  public options: SelectOption[] = []

  public type_options = [
    new SelectOption('Advertiser', 'advertiser'),
    new SelectOption('Publisher', 'publisher'),
    new SelectOption('Agency', 'agency'),
    new SelectOption('Station', 'station'),
  ]

  public synchronized: boolean = false

  public get api() {
    return new Api(false)
  }

  @Mutation
  public setOptions(options: SelectOption[]) {
    this.options = options
  }

  @Mutation
  public setSynchronized(status: boolean) {
    this.synchronized = status
  }

  @Mutation
  public update(publishers: Publisher | Publisher[]) {
    // Set input to array
    let data: Array<Publisher> = []
    if (publishers instanceof Publisher) {
      data.push(publishers)
    } else {
      data = publishers
    }

    let result = this.data

    // Update Module Data
    data.forEach((publisher: Publisher) => {
      let found: boolean = false
      result = result.map((obj: Publisher) => {
        if (obj.name == publisher.name) {
          found = true
          return publisher
        }
        return obj
      })

      if (!found) {
        result.push(publisher)
      }
    })

    this.data = result

    // Update Module Options
    let { options } = this
    data.forEach((publisher: Publisher) => {
      let found: boolean = false
      options = options.map((o: SelectOption) => {
        if (o.value == publisher.name) {
          found = true
          return publisher.toOption()
        }
        return o
      })

      if (!found) {
        options.push(publisher.toOption())
      }
    })

    this.options = options
  }

  @Mutation
  public delete(publishers: Publisher | Publisher[]) {
    // Set input to array
    let data: Array<Publisher> = []
    if (publishers instanceof Publisher) {
      data.push(publishers)
    } else {
      data = publishers
    }

    let result = this.data

    // Remove Object from Module Data
    data.forEach((publisher: Publisher) => {
      result = result.filter((obj: Publisher) => obj.name != publisher.name)
    })

    this.data = result

    // Remove Object from Module Options
    let { options } = this
    data.forEach((publisher: Publisher) => {
      options = options.filter((obj: SelectOption) => obj.value != publisher.name)
    })

    this.options = options
  }

  @Action
  public async find(name: string): Promise<Publisher | null> {
    return new Promise(resolve => {
      if (!name) resolve(null)
      const o = this.data.find(publisher => publisher.name === name)

      if (o instanceof Publisher) {
        resolve(o)
      } else {
        return this.api
          .get(`publisher/${name}`)
          .then(response => {
            // Parse & cache data
            const data = Publisher.toObject(response.data.result.publisher)

            if (data instanceof Publisher) {
              resolve(data)
            }
          })
          .catch(() => {
            resolve(null)
          })
      }
    })
  }

  @Action
  public async paginate(options: PaginateOptions) {
    return this.api
      .get('publishers/paginate', options)
      .then(response => {
        // Parse & cache data
        const data = Publisher.toObjectList(response.data.result.publishers)

        return {
          records: response.data.result.records,
          data,
        }
      })
      .catch(() => ({
        records: 0,
        data: [],
      }))
  }

  /**
   * @deprecated No longer needed after Elasticsearch implementation. Use searchOptions instead.
   */
  @Action
  public async syncOptions() {
    this.setSynchronized(true)
    return this.api
      .get('publishers/option')
      .then(response => {
        // Parse & cache data
        const data = SelectOption.toObjectList(response.data.result.options)

        this.context.commit('setOptions', data)
      })
      .catch(() => ({
        records: 0,
        data: [],
      }))
  }

  @Action
  public async searchOptions(query: any) {
    return this.api
      .get('publishers/search/option', query)
      .then(response => SelectOption.toObjectList(response.data.result.options))
      .catch(() => [])
  }
}
